/**
 * ArrangementIntelligence
 * V11 Feature 1: Dynamic Arrangement Intelligence
 * 
 * Manages musical "breathing" by analyzing touch density
 * and creating dynamic contrast automatically.
 * 
 * - Auto-Thinning: When density is high, probabilistically drops notes to create space
 * - Comeback Moments: After silence, the next touch triggers fuller voicing
 * - Dynamic Orchestration: Suggests layer presence based on intensity
 */

export interface ArrangementHint {
    shouldDropNote: boolean;      // Auto-thinning decision
    isComebackMoment: boolean;    // Dramatic re-entry after silence
    velocityBoost: number;        // 0-0.3 boost for comeback
    layerPresence: {
        pad: boolean;             // Should ghost pad be present
        octaveDoubling: boolean;  // Should double bass octaves
    };
    densityLevel: 'sparse' | 'normal' | 'dense';
}

export class ArrangementIntelligence {
    // Touch timing history for density analysis
    private touchTimestamps: number[] = [];
    private readonly WINDOW_SIZE = 8;  // Rolling window for density
    private readonly DENSE_THRESHOLD_MS = 250;  // < 250ms between touches = dense
    private readonly SPARSE_THRESHOLD_MS = 800; // > 800ms between touches = sparse

    // Silence tracking for comeback moments
    private lastTouchTime: number = 0;
    private readonly COMEBACK_SILENCE_MS = 1200; // 1.2 seconds of silence triggers comeback

    // Pattern tracking
    private consecutiveDrops: number = 0;
    private readonly MAX_CONSECUTIVE_DROPS = 2; // Never drop more than 2 in a row

    constructor() {
        this.reset();
    }

    reset(): void {
        this.touchTimestamps = [];
        this.lastTouchTime = Date.now();
        this.consecutiveDrops = 0;
    }

    /**
     * Analyze touch and return arrangement hints.
     * Call this BEFORE generating notes for a touch.
     */
    getHint(): ArrangementHint {
        const now = Date.now();
        const timeSinceLastTouch = now - this.lastTouchTime;

        // Record this touch
        this.touchTimestamps.push(now);
        if (this.touchTimestamps.length > this.WINDOW_SIZE) {
            this.touchTimestamps.shift();
        }
        this.lastTouchTime = now;

        // === Density Analysis ===
        const densityLevel = this.analyzeDensity();

        // === Comeback Detection ===
        const isComebackMoment = timeSinceLastTouch > this.COMEBACK_SILENCE_MS;

        // === Auto-Thinning Decision ===
        let shouldDropNote = false;
        if (densityLevel === 'dense' && !isComebackMoment) {
            // When dense, probabilistically drop notes to create breathing room
            // But never drop if we just came back from silence
            // And never drop more than 2 in a row
            if (this.consecutiveDrops < this.MAX_CONSECUTIVE_DROPS) {
                // 25% chance to drop when dense
                shouldDropNote = Math.random() < 0.25;
            }
        }

        // Track consecutive drops
        if (shouldDropNote) {
            this.consecutiveDrops++;
        } else {
            this.consecutiveDrops = 0;
        }

        // === Velocity Boost for Comeback ===
        let velocityBoost = 0;
        if (isComebackMoment) {
            // Longer silence = bigger comeback boost (capped at 0.3)
            velocityBoost = Math.min(0.3, (timeSinceLastTouch - this.COMEBACK_SILENCE_MS) / 5000);
        }

        // === Layer Presence ===
        const layerPresence = this.calculateLayerPresence(densityLevel, isComebackMoment);

        return {
            shouldDropNote,
            isComebackMoment,
            velocityBoost,
            layerPresence,
            densityLevel
        };
    }

    // THE VOID STATE
    private lastVoidTime: number = 0;
    private readonly VOID_COOLDOWN = 45000; // 45s between drops

    /**
     * Check if we should trigger "The Void" (Dramatic Silence Drop).
     * @param intensity Current touch intensity (velocity)
     */
    checkForVoid(intensity: number): boolean {
        const now = Date.now();

        // 1. Needs High Intensity (Climax)
        if (intensity < 0.8) return false;

        // 2. Cooldown check
        if (now - this.lastVoidTime < this.VOID_COOLDOWN) return false;

        // 3. Must be Dense environment (contrast is key)
        if (this.getCurrentDensity() !== 'dense') return false;

        // 4. Probability (Rare event)
        // 5% chance per high-intensity touch when eligible
        if (Math.random() < 0.05) {
            this.lastVoidTime = now;
            if (process.env.NODE_ENV === 'development') console.log('[ArrangementIntelligence] ⚫ THE VOID TRIGGERED ⚫');
            return true;
        }

        return false;
    }

    /**
     * Analyze touch timing to determine current density level.
     */
    private analyzeDensity(): 'sparse' | 'normal' | 'dense' {
        if (this.touchTimestamps.length < 2) {
            return 'normal';
        }

        // Calculate average interval between recent touches
        let totalInterval = 0;
        for (let i = 1; i < this.touchTimestamps.length; i++) {
            totalInterval += this.touchTimestamps[i] - this.touchTimestamps[i - 1];
        }
        const avgInterval = totalInterval / (this.touchTimestamps.length - 1);

        if (avgInterval < this.DENSE_THRESHOLD_MS) {
            return 'dense';
        } else if (avgInterval > this.SPARSE_THRESHOLD_MS) {
            return 'sparse';
        }
        return 'normal';
    }

    /**
     * Determine which layers should be active based on intensity.
     */
    private calculateLayerPresence(
        density: 'sparse' | 'normal' | 'dense',
        isComebackMoment: boolean
    ): { pad: boolean; octaveDoubling: boolean } {
        // Comeback moments get full orchestration
        if (isComebackMoment) {
            return { pad: true, octaveDoubling: true };
        }

        switch (density) {
            case 'sparse':
                // Solo, intimate feel - no extra layers
                return { pad: false, octaveDoubling: false };
            case 'normal':
                // Light pad support
                return { pad: true, octaveDoubling: false };
            case 'dense':
                // Full orchestration to fill the energy
                return { pad: true, octaveDoubling: true };
        }
    }

    /**
     * Get the current density level without recording a touch.
     * Useful for ghost musician decisions.
     */
    getCurrentDensity(): 'sparse' | 'normal' | 'dense' {
        return this.analyzeDensity();
    }

    /**
     * Check if we're currently in a "silent" state (potential comeback coming).
     */
    isInSilence(): boolean {
        return (Date.now() - this.lastTouchTime) > this.COMEBACK_SILENCE_MS;
    }
}
